import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, createVNode as _createVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, mergeProps as _mergeProps, withDirectives as _withDirectives, vShow as _vShow, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue";
const _hoisted_1 = ["textContent"];
const _hoisted_2 = ["disabled", "aria-disabled", "tabindex", "aria-expanded", "aria-controls"];
const _hoisted_3 = {
    key: 2,
    class: "rec-flyout-indicator"
};
const _hoisted_4 = ["id", "role"];
import { vTooltip } from "@/common/directives/tip-directive";
import { onClickOutside, useVModel } from "@vueuse/core";
import { v4 as uuid } from "uuid";
import { useDropdownAccessibility } from "@/common/composables/dropdown-accessibility";
import { useDropdownPosition } from "@/common/composables/dropdown-position";
import { computed, ref, useSlots, watchEffect } from "vue";
// Components
import SvgIcon from "@/common/components/SvgIcon.vue";
import { MenuItemType } from "@/common/composables/dropdown-accessibility";
//Props
export default /*@__PURE__*/ _defineComponent({
    __name: 'DropdownMenu',
    props: {
        absolute: { type: Boolean },
        appendIcon: { default: "" },
        appendIconInside: { default: "" },
        autoClose: { type: Boolean, default: true },
        buttonClass: { default: "" },
        buttonTabindex: { default: undefined },
        disabled: { type: Boolean },
        ariaDisabled: { type: Boolean },
        error: { default: "" },
        iconSrc: { default: "/svg/svg-defs.svg" },
        menuClass: { default: "" },
        menuMaxHeight: { default: 0 },
        menuMinHeight: { default: 0 },
        menuPosition: { default: "" },
        menuWidth: { default: 0 },
        menuWidthMatchButton: { type: Boolean },
        modelValue: { type: Boolean },
        offsetY: { default: 3 },
        placement: { default: "auto" },
        prependIcon: { default: "" },
        prependIconInside: { default: "" },
        buttonOnly: { type: Boolean },
        type: { default: "custom" },
        useVShow: { type: Boolean },
        windowPadding: { default: 20 },
        ignoreClickOutside: { default: () => [] },
        active: { type: Boolean },
        tooltip: { default: "" },
        align: { default: undefined },
        lockPositionOnOpen: { type: Boolean, default: false },
        buttonAttrs: { default: () => ({}) },
        menuRole: { default: "menu" },
        menuId: {},
        lazy: { type: Boolean }
    },
    emits: ["update:modelValue", "click"],
    setup(__props, { expose: __expose, emit: __emit }) {
        /********************************************
         * DropdownMenu Component
         * Provides a clickable trigger and a positioned menu.
         *
         * Pass in two slots for the menu content
         *  <template #button></template>
         *  <template #menu></template>
         *
         * Props:
         *  absolute: Controls whether the menu is positioned absolutely (default: fixed positioning). Can be used to fix issues with menus inside scrollable containers
         *  appendIcon: Icon to be appended to the button
         *  appendIconInside: Icon to be appended to the button text
         *  autoClose: Controls whether the menu automatically closes when an item is clicked. To handle this selectively, prevent the propogation of click events
         *  buttonClass: Classes to be applied to the button element
         *  disabled: Disables the button
         *  ariaDisabled: Marks the button aria-disabled (stays focusable, tooltip still fires, click is a no-op) instead of natively disabled
         *  error: Error message to be displayed
         *  iconSrc: Path to the svg icon sprite
         *  ignoreClickOutside: Ignore clicks outside the menu on the specified element (valid css selector)
         *  menuClass: Classes to be applied to the menu element
         *  menuMaxHeight: Maximum height of the menu
         *  menuPosition: Controls the position of the menu relative to the button
         *  menuWidth: Width of the menu
         *  modelValue: Controls the open state of the menu
         *  offsetY: Vertical offset of the menu from the button
         *  placement: Controls the vertical placement of the menu. Options are "above", "below", and "auto" (default)
         *  prependIcon: Icon to be prepended to the button
         *  prependIconInside: Icon to be prepended to the button text
         *  buttonOnly: Controls whether the menu is in static mode
         *  type: Controls the type of dropdown menu. Options are "button", "flyout", "select", and "custom"
         *  useVShow: Controls whether the menu is shown using v-show or v-if
         *  windowPadding: Padding around the menu
         *
         * Emits:
         *  modelValue: indicates that the open state of the menu has changed
         *
         *  <DropdownMenu type="button" button-class="btn-primary">
         *    <template #button>Options</template>
         *    <template #menu>
         *      <button @click="handleEdit">Edit</button>
         *      <DropdownMenu type="flyout">
         *        <template #button>
         *          Flyout Button Label
         *        </template>
         *        <template #menu>
         *          <button type="button" class="rec-dropdown-menu__item">
         *            Flyout Menu Button Label
         *          </button>
         *        </template>
         *      </DropdownMenu>
         *    </template>
         *  </DropdownMenu>
         *********************************************/
        const props = __props;
        //Emits
        const emit = __emit;
        const model = useVModel(props, "modelValue", emit, {
            defaultValue: false,
            passive: true
        });
        const key = uuid();
        const dropdown = ref(null);
        const menu = ref();
        const menuButton = ref(null);
        const slots = useSlots();
        const resolvedMenuId = computed(() => props.menuId || `rec-dropdown-menu-${key}`);
        // Rendered ≠ slotted: aria-controls needs a real DOM element, aria-expanded only needs the slot.
        // useVShow keeps a slotted menu mounted while collapsed; without a menu slot there is nothing to name.
        const menuRendered = computed(() => !!slots.menu && (props.useVShow || model.value));
        const isButton = computed(() => props.type == "button");
        const isFlyout = computed(() => props.type == "flyout");
        const isSelect = computed(() => props.type == "select");
        const isFilterChip = computed(() => props.type == "filter-chip");
        const menuHeight = ref(600);
        const { menuStyle } = useDropdownPosition(menuButton, menu, {
            open: model,
            isFlyout: () => isFlyout.value,
            isSelect: () => isSelect.value,
            absolute: () => props.absolute,
            menuPosition: () => props.menuPosition,
            placement: () => props.placement,
            offsetY: () => props.offsetY,
            menuWidth: () => props.menuWidth,
            menuWidthMatchButton: () => props.menuWidthMatchButton,
            windowPadding: () => props.windowPadding,
            menuMaxHeight: () => props.menuMaxHeight,
            menuMinHeight: () => props.menuMinHeight,
            align: () => props.align,
            lockPositionOnOpen: () => props.lockPositionOnOpen
        });
        const classes = computed(() => ({
            "rec-dropdown": true,
            "rec-dropdown--open": model.value,
            "rec-dropdown--absolute": props.absolute,
            "rec-dropdown--with-icons": props.prependIcon || props.appendIcon,
            "rec-dropdown--with-icons-inside": props.prependIconInside || props.appendIconInside
        }));
        const buttonClasses = computed(() => {
            const classes = [];
            const builtin = isFlyout.value
                ? {
                    "rec-dropdown-menu__item rec-dropdown-menu__item--flyout": true,
                    "rec-dropdown-menu__item--active": model.value
                }
                : {
                    "rec-dropdown__button": true,
                    "rec-dropdown__button--has-tooltip": !!props.tooltip,
                    [`rec-dropdown__button--${props.type}`]: true,
                    "rec-dropdown__button--active": props.active, // Active state (e.g. values are selected in a RecSelect component)
                    "rec-dropdown__button--disabled": props.disabled,
                    btn: isButton.value,
                    "form-control": isSelect.value,
                    "rec-filter-chip": isFilterChip.value
                };
            classes.push(builtin);
            if (props.buttonClass)
                classes.push(props.buttonClass);
            return classes;
        });
        const menuClasses = computed(() => ["rec-dropdown-menu", props.menuClass || ""]);
        const lazyWiringArmed = ref(false);
        const armLazyWiring = () => {
            lazyWiringArmed.value = true;
        };
        const handleToggleMenuOpen = (state) => {
            // aria-disabled blocks opening only — closing (clickOutside / autoClose / Escape) must still work.
            const willOpen = state ?? !model.value;
            if (props.ariaDisabled && willOpen)
                return;
            if (props.buttonOnly) {
                emit("click");
                return;
            }
            model.value = state !== undefined ? state : !model.value;
        };
        const onMenuClick = () => {
            if (!props.autoClose)
                return;
            handleToggleMenuOpen(false);
        };
        watchEffect((onCleanup) => {
            // `lazy` call sites pay for the document-level listeners only once the trigger is touched.
            if (props.lazy && !lazyWiringArmed.value && !model.value)
                return;
            const stopClickOutside = onClickOutside(menu, () => {
                if (!model.value)
                    return;
                handleToggleMenuOpen(false);
            }, {
                ignore: [menuButton, ...props.ignoreClickOutside]
            });
            const eventListenerDisposer = useDropdownAccessibility({
                eventTargetEl: dropdown,
                menu: menu,
                isOpen: model,
                toggleSubMenu: handleToggleMenuOpen,
                menuToggleBtn: menuButton,
                submenu: isFlyout.value ? menu : undefined,
                menuItemBtnType: isFlyout.value ? MenuItemType.FLYOUT : MenuItemType.DROPDOWN
            });
            onCleanup(() => {
                stopClickOutside();
                eventListenerDisposer();
            });
        });
        __expose({
            menuButton,
            el: dropdown
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "dropdown",
                ref: dropdown,
                key: _unref(key),
                class: _normalizeClass(classes.value)
            }, [
                (__props.error)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: "label label-danger rc-mb5",
                        textContent: _toDisplayString(__props.error)
                    }, null, 8, _hoisted_1))
                    : _createCommentVNode("", true),
                (__props.prependIcon)
                    ? (_openBlock(), _createBlock(SvgIcon, {
                        key: 1,
                        icon: __props.prependIcon,
                        src: __props.iconSrc,
                        class: "rec-dropdown__prepend-icon"
                    }, null, 8, ["icon", "src"]))
                    : _createCommentVNode("", true),
                _withDirectives((_openBlock(), _createElementBlock("button", _mergeProps({
                    ref_key: "menuButton",
                    ref: menuButton,
                    class: buttonClasses.value,
                    disabled: !!__props.disabled,
                    "aria-disabled": __props.ariaDisabled || undefined,
                    tabindex: __props.buttonTabindex,
                    "aria-haspopup": "true",
                    "aria-expanded": _ctx.$slots.menu ? _unref(model) : undefined,
                    "aria-controls": menuRendered.value ? resolvedMenuId.value : undefined,
                    type: "button"
                }, __props.buttonAttrs, {
                    onClick: _cache[0] || (_cache[0] = _withModifiers(($event) => (handleToggleMenuOpen()), ["stop"])),
                    onFocus: armLazyWiring
                }), [
                    (__props.prependIconInside)
                        ? (_openBlock(), _createBlock(SvgIcon, {
                            key: 0,
                            icon: __props.prependIconInside,
                            src: __props.iconSrc,
                            class: "rec-dropdown__prepend-icon-inside"
                        }, null, 8, ["icon", "src"]))
                        : _createCommentVNode("", true),
                    _renderSlot(_ctx.$slots, "button", _normalizeProps(_guardReactiveProps({ open: _unref(model) }))),
                    (__props.appendIconInside)
                        ? (_openBlock(), _createBlock(SvgIcon, {
                            key: 1,
                            icon: __props.appendIconInside,
                            src: __props.iconSrc,
                            class: "rec-dropdown__append-icon-inside"
                        }, null, 8, ["icon", "src"]))
                        : _createCommentVNode("", true),
                    (isFlyout.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _createElementVNode("div", null, [
                                _createVNode(SvgIcon, {
                                    icon: "icon-arrow-right",
                                    class: "rec-flyout-indicator__chevron"
                                })
                            ])
                        ]))
                        : _createCommentVNode("", true)
                ], 16, _hoisted_2)), [
                    [_unref(vTooltip), __props.tooltip]
                ]),
                (__props.appendIcon)
                    ? (_openBlock(), _createBlock(SvgIcon, {
                        key: 2,
                        icon: __props.appendIcon,
                        src: __props.iconSrc,
                        class: "rec-dropdown__append-icon"
                    }, null, 8, ["icon", "src"]))
                    : _createCommentVNode("", true),
                (menuRendered.value)
                    ? _withDirectives((_openBlock(), _createElementBlock("div", {
                        key: 3,
                        ref_key: "menu",
                        ref: menu,
                        id: resolvedMenuId.value,
                        class: _normalizeClass(menuClasses.value),
                        style: _normalizeStyle(_unref(menuStyle)),
                        role: __props.menuRole,
                        onClick: _withModifiers(onMenuClick, ["stop"])
                    }, [
                        _renderSlot(_ctx.$slots, "menu", _normalizeProps(_guardReactiveProps({ toggleMenu: handleToggleMenuOpen, menuHeight: menuHeight.value })))
                    ], 14, _hoisted_4)), [
                        [_vShow, __props.useVShow ? _unref(model) && _ctx.$slots.menu : true]
                    ])
                    : _createCommentVNode("", true)
            ], 2));
        };
    }
});
